import type { TefComs4csMicroFrontend } from '@mfe-4cs/components/tef-coms4cs';
import { html, TemplateResult, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { ConsentGroupStore } from '@mfe-4cs/state/ConsentGroupStore';
import comsStore from '@mfe-4cs/state/ComsStore';
import { replaceElements } from '../util';

function getGroupText(this: TefComs4csMicroFrontend, textGroup: ConsentGroupStore): TemplateResult {
  return textGroup.text
    ? html` ${unsafeHTML(replaceElements.bind(this)(textGroup.text))} `
    : nothing as unknown as TemplateResult;
}

// start oneclick checkbox, text and container
function getOneclickHeader(this: TefComs4csMicroFrontend, oGroup: ConsentGroupStore): TemplateResult {
  return html`
    <one-cluster gap="sm" no-wrap x="start" y="start">
      <one-checkbox
        .checked=${oGroup.areAllConsentsAndFeaturesGranted}
        id="group-${oGroup.id}"
        type="checkbox"
        @click=${(e: PointerEvent) => { oGroup.oneClickToggle(e); }}
        ?disabled="${comsStore.identity?.readonly ?? false}"
        >
      </one-checkbox>
      <label for="group-${oGroup.id}">
        ${unsafeHTML(replaceElements.bind(this)(oGroup.text))}
      </label>
    </one-cluster>
  `;
}

function getGroupOneclickConsentsContainer(this: TefComs4csMicroFrontend, containerGroup: ConsentGroupStore)
  : TemplateResult {
  const consentTemplate = this.getTemplateFn('consentTemplate');

  return html`
    <one-accordion
      ?is-open="${containerGroup.collapsibleOpen}"
      cloak
      context=${comsStore.config?.styleContext ?? ''}
      id=${containerGroup.name || 'noname'}
    >
      <one-accordion-item once>
        <div slot="summary">
          ${containerGroup.oneclickCollapsible /* eslint-disable @typescript-eslint/indent */
            ? unsafeHTML(containerGroup.oneclickCollapsible)
            : 'Wenn ich einzelne Kontaktkanäle und Verarbeitungsvorgänge von meiner '
              + 'Auswahl ausschließen möchte, kann ich hier gesonderte Einwilligungen erteilen.'
          /* eslint-enable @typescript-eslint/indent */}
        </div>
        <one-stack gap="md">
          ${containerGroup.consents.map((consent) => consentTemplate(consent))}
        </one-stack>
      </one-accordion-item>
    </one-accordion>
  `;
}
// end oneclick text and container

// denyAll - OCW checkbox
function getWithdrawal(ocwGroup: ConsentGroupStore): TemplateResult {
  if (!ocwGroup.denyall || ocwGroup.oneclick) {
    return nothing as unknown as TemplateResult;
  }

  const checkboxId = `deny_check_${ocwGroup.id}`;

  return html`
    <one-cluster gap="sm" no-wrap>
      <one-checkbox
        id=${checkboxId}
        type="checkbox"
        .checked=${ocwGroup.areAllConsentsDenied}
        @click=${(e: PointerEvent) => ocwGroup.denyAll(e)}>
      </one-checkbox>
      <label for=${checkboxId}>
        ${unsafeHTML(ocwGroup.denyall)}
      </label>
    </one-cluster>
  `;
}

export function groupTemplate(this: TefComs4csMicroFrontend, group: ConsentGroupStore): TemplateResult {
  const consentTemplate = this.getTemplateFn('consentTemplate');

  return html`
  <one-content>
    <one-stack gap="md">
      <one-heading level="h4">
      ${unsafeHTML(replaceElements.bind(this)(group.header))}
      </one-heading>
      <one-text as="p">
        ${group.oneclick /* eslint-disable @typescript-eslint/indent */
          ? getOneclickHeader.bind(this)(group)
          : getGroupText.bind(this)(group)
          /* eslint-enable @typescript-eslint/indent */}
      </one-text>
      <one-stack gap="sm">
        ${group.oneclick /* eslint-disable @typescript-eslint/indent */
          ? getGroupOneclickConsentsContainer.bind(this)(group)
          : group.consents.map((consent) => consentTemplate(consent))
          /* eslint-enable @typescript-eslint/indent */}
      </one-stack>
      ${group.footer ? html`
        <one-text as="p">
          ${unsafeHTML(replaceElements.bind(this)(group.footer))}
        </one-text>
      ` : nothing}
      ${getWithdrawal(group)}
      </div>
      <one-divider variant="default"></one-divider>
    </one-stack>
  </one-content>
`;
}

export function groupTemplateOverview(this: TefComs4csMicroFrontend, group: ConsentGroupStore): TemplateResult {
  const filteredConsents = group.consents
    .filter(({ granted = false }) => granted === true);

  const consentTemplateOverview = this.getTemplateFn('consentTemplateOverview');

  return html`
    <one-stack gap="xxs">
      <one-heading level="h4">
        ${unsafeHTML(replaceElements.bind(this)(group.header))}
      </one-heading>
      <div>
        ${(filteredConsents.length === 0) /* eslint-disable @typescript-eslint/indent */
          ? html`
            <one-paper background="legal-2" padding="sm">
              ${group.denyallView ?? 'es liegen keine Erlaubnisse / Einwilligungen vor'}
            </one-paper>`
          : html`
            <one-text as="div">
              ${group.consents.map((consent) => (
                consent.granted ? consentTemplateOverview(consent) : nothing))}
            </one-text>`
        /* eslint-enable @typescript-eslint/indent */}
      </div>
    </one-stack>
    <one-divider variant="default"></one-divider>
  `;
}
